import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgressOverlay, ProgressOverlayStatus } from '@fs/zion-ui'
import { getSessionId } from '@fs/zion-session'
import { trackEvent } from '@fs/zion-analytics'
import axios from '@fs/zion-axios'
import { getCollections, uploadTree } from '../lib/my-trees-service'
import { getAnalyticsObject } from '../lib/my-trees-helpers'

const CancelToken = axios.CancelToken
const { IN_PROGRESS, SUCCESSFUL, FAILED } = ProgressOverlayStatus

// GEDCOM error codes
const GEDCOM_TOO_LARGE = 413
const GEDCOM_IS_AF_CORRECTION_FILE = 450
const GEDCOM_IS_AF = 451
const GEDCOM_BAD_FORMAT = 452
const GEDCOM_NO_TRAILER = 453
const GEDCOM_HAS_NO_INDIVIDUALS = 454

/**
 * Custom hook for handling tree upload functionality.
 * @param {object} options - Configuration options
 * @param {boolean} options.fetchCollectionsOnMount - Whether to fetch collections when hook mounts
 * @param {Function} options.onUploadSuccess - Callback function called on successful upload
 * @returns {object} Upload state and handlers
 */
export function useTreeUpload({ fetchCollectionsOnMount = false, onUploadSuccess = () => {} } = {}) {
  const [t] = useTranslation()
  const { createProgressItem } = useProgressOverlay()
  const update = useRef()
  const sessionId = getSessionId()
  // This needs to state undefined as initial value
  const [collections, setCollections] = useState()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const handleUploadProgress =
    (fileName) =>
    ({ loaded, total }) => {
      const percent = (loaded / total) * 100
      if (percent < 100) {
        update.current?.({ percent })
      } else {
        update.current?.({
          message: t('tree-import.uploaded-file.label', { fileName }),
          status: SUCCESSFUL,
          percent,
        })
      }
    }

  const fetchCollections = useCallback(() => {
    if (!collections) {
      getCollections(sessionId)
        .then(setCollections)
        .catch((err) => {
          console.error('Failed to fetch collections:', err)
          setError(t('tree-import.upload-trees.error.503.web_service_down_error'))
        })
    }
  }, [collections, sessionId, t])

  const handleUpload = ({ treeFile, treeName, treeDescription, collectionId }, overlay) => {
    const sourceRef = CancelToken.source()

    update.current = createProgressItem(
      {
        message: t('tree-import.uploading-file.label', { fileName: treeName || treeFile.name }),
        status: IN_PROGRESS,
        percent: 0,
      },
      () => {
        sourceRef.cancel('Upload canceled by user.')
        trackEvent(getAnalyticsObject('Upload Dialog: Cancel'))
      }
    )

    setUploading(true)

    uploadTree(
      sessionId,
      treeFile,
      treeName || treeFile.name,
      treeDescription,
      collectionId,
      handleUploadProgress(treeName || treeFile.name),
      sourceRef.token
    )
      .then(() => {
        overlay.close()
        onUploadSuccess()
        return undefined
      })
      .catch((err) => {
        const errorCode = err && err.response && err.response.status

        switch (errorCode) {
          case GEDCOM_TOO_LARGE:
            update.current?.({
              message: t('tree-import.upload-trees.error.413.file_too_large'),
              status: FAILED,
            })
            break
          case GEDCOM_IS_AF_CORRECTION_FILE:
            update.current?.({
              message: t('tree-import.upload-trees.error.450.af_correction_file'),
              status: FAILED,
            })
            break
          case GEDCOM_IS_AF:
            update.current?.({
              message: t('tree-import.upload-trees.error.451.ancestral_file'),
              status: FAILED,
            })
            break
          case GEDCOM_BAD_FORMAT:
            update.current?.({
              message: t('tree-import.upload-trees.error.452.bad_format_header'),
              status: FAILED,
            })
            break
          case GEDCOM_NO_TRAILER:
            update.current?.({
              message: t('tree-import.upload-trees.error.453.bad_format_trailer'),
              status: FAILED,
            })
            break
          case GEDCOM_HAS_NO_INDIVIDUALS:
            update.current?.({
              message: t('tree-import.upload-trees.error.454.no_individuals'),
              status: FAILED,
            })
            break
          default:
            update.current?.({
              message: t('tree-import.upload-trees.error.upload_error'),
              status: FAILED,
            })
        }
      })
      .finally(() => setUploading(false))
  }

  // Effect for fetching collections based on configuration
  useEffect(() => {
    if (fetchCollectionsOnMount) {
      fetchCollections()
    }
  }, [fetchCollectionsOnMount, fetchCollections])

  return {
    collections,
    uploading,
    error,
    setError,
    handleUpload,
    fetchCollections,
  }
}
